import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '../services/api';

interface CurrencyInfo {
  country: string;
  currency: string;
  symbol: string;
  rate: number;
}

interface CurrencyContextType {
  currency: CurrencyInfo;
  formatPrice: (usdPrice: string | number) => string;
  loading: boolean;
}

const defaultCurrency: CurrencyInfo = {
  country: 'US',
  currency: 'USD',
  symbol: '$',
  rate: 1,
};

const CurrencyContext = createContext<CurrencyContextType>({
  currency: defaultCurrency,
  formatPrice: (p) => `$${p}`,
  loading: true,
});

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currency, setCurrency] = useState<CurrencyInfo>(defaultCurrency);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/site/currency/')
      .then(res => {
        setCurrency({
          country: res.data.country,
          currency: res.data.currency,
          symbol: res.data.symbol,
          rate: parseFloat(res.data.rate),
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const formatPrice = useCallback((usdPrice: string | number): string => {
    const numPrice = typeof usdPrice === 'string' ? parseFloat(usdPrice) : usdPrice;
    if (isNaN(numPrice)) return `${currency.symbol}0.00`;
    const converted = numPrice * currency.rate;
    // For currencies with large values (JPY, KRW, NGN, etc.), skip decimals
    const noDecimalCurrencies = ['JPY', 'KRW', 'VND', 'IDR', 'UGX', 'CLP'];
    if (noDecimalCurrencies.includes(currency.currency)) {
      return `${currency.symbol}${Math.round(converted).toLocaleString()}`;
    }
    return `${currency.symbol}${converted.toFixed(2)}`;
  }, [currency]);

  return (
    <CurrencyContext.Provider value={{ currency, formatPrice, loading }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
